const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);

    display: inline-flex;
    font-family: var(--ctl-font);
    vertical-align: middle;
  }

  .indicator{
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-2);
    line-height: 1;
  }

  .dot{
    width: var(--dot-size, var(--ctl-space-2));
    height: var(--dot-size, var(--ctl-space-2));
    border-radius: var(--ctl-radius-full);
    background: var(--dot-color);
    flex-shrink: 0;
    position: relative;
  }

  /* pulse animation for online/active states */
  :host([pulse]) .dot::before{
    content: "";
    position: absolute;
    inset: calc(-1 * var(--ctl-space-1));
    border-radius: var(--ctl-radius-full);
    background: var(--dot-color);
    opacity: 0;
    animation: ctlPulse 2s ease-out infinite;
  }

  @keyframes ctlPulse{
    0%{
      transform: scale(0.8);
      opacity: var(--ctl-opacity-hover);
    }
    50%{
      transform: scale(1.4);
      opacity: 0;
    }
    100%{
      transform: scale(1.4);
      opacity: 0;
    }
  }

  .label{
    font-size: var(--font-size, var(--ctl-font-size-md));
    font-weight: var(--ctl-font-weight-semibold);
    color: var(--ctl-color-text);
    letter-spacing: var(--ctl-tracking-tight);
  }

  /* sizes */
  :host([size="sm"]){
    --dot-size: var(--ctl-space-1-5);
    --font-size: var(--ctl-font-size-sm);
  }

  :host(:not([size]), [size="md"]){
    --dot-size: var(--ctl-space-2);
    --font-size: var(--ctl-font-size-md);
  }

  :host([size="lg"]){
    --dot-size: var(--ctl-space-2-5);
    --font-size: var(--ctl-font-size-base);
  }

  /* status -> dot color */
  :host([status="neutral"]) { --dot-color: var(--ctl-status-neutral, var(--ctl-color-muted)); }
  :host([status="info"])    { --dot-color: var(--ctl-status-info, var(--ctl-color-info)); }
  :host([status="success"]) { --dot-color: var(--ctl-status-success, var(--ctl-color-success)); }
  :host([status="warning"]) { --dot-color: var(--ctl-status-warning, var(--ctl-color-warning)); }
  :host([status="danger"])  { --dot-color: var(--ctl-status-danger, var(--ctl-color-danger)); }
  :host([status="offline"]) { --dot-color: var(--ctl-status-offline, var(--ctl-color-muted)); }
  :host(:not([status]))     { --dot-color: var(--ctl-status-neutral, var(--ctl-color-muted)); }

  /* muted label variant */
  :host([muted]) .label{
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-normal);
  }
</style>

<span class="indicator" part="indicator">
  <span class="dot" part="dot" aria-hidden="true"></span>
  <span class="label" part="label">
    <slot></slot>
  </span>
</span>
`;

export class CtlStatusIndicator extends HTMLElement {
  static get observedAttributes() {
    return ["status", "size", "pulse", "muted"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("status")) this.setAttribute("status", "neutral");
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");
    this._normalize();
  }

  attributeChangedCallback() {
    this._normalize();
  }

  // attrs
  get status() { return this.getAttribute("status") || "neutral"; }
  set status(v) { this.setAttribute("status", String(v || "neutral")); }

  get size() { return this.getAttribute("size") || "md"; }
  set size(v) { this.setAttribute("size", String(v || "md")); }

  get pulse() { return this.hasAttribute("pulse"); }
  set pulse(v) { v ? this.setAttribute("pulse", "") : this.removeAttribute("pulse"); }

  get muted() { return this.hasAttribute("muted"); }
  set muted(v) { v ? this.setAttribute("muted", "") : this.removeAttribute("muted"); }

  _normalize() {
    const status = this.status;
    if (!["neutral", "info", "success", "warning", "danger", "offline"].includes(status)) {
      this.setAttribute("status", "neutral");
    }

    const size = this.size;
    if (!["sm", "md", "lg"].includes(size)) {
      this.setAttribute("size", "md");
    }
  }
}

customElements.define("ctl-status-indicator", CtlStatusIndicator);
